/* eslint-disable react/prop-types */
import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, User, Mail, Phone, Save, Lock, AtSign } from "lucide-react";
import { toast } from "sonner";
import { useCustomerAuth } from "../../lib/useCustomerAuth";

const PRIMARY = "#6B2D5C";
const SECONDARY = "#EC1F7C";

/**
 * Modal d'édition des coordonnées d'un client connecté.
 * - PUT /api/customer/me  → first_name, last_name, phone, accept_newsletter
 * - L'email reste en lecture seule (verrou de connexion).
 */
export default function EditProfileModal({ open, onClose }) {
  const { customer, updateProfile } = useCustomerAuth();
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    phone: "",
    accept_newsletter: false,
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open && customer) {
      setForm({
        first_name: customer.first_name || "",
        last_name: customer.last_name || "",
        phone: customer.phone || "",
        accept_newsletter: !!customer.accept_newsletter,
      });
      setError("");
    }
  }, [open, customer]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await updateProfile(form);
      toast.success("Coordonnées mises à jour");
      onClose();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Erreur lors de la mise à jour";
      setError(msg);
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  return createPortal(
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
        data-testid="edit-profile-overlay"
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          transition={{ type: "spring", damping: 24, stiffness: 280 }}
          className="relative w-full max-w-md bg-white rounded-3xl shadow-2xl overflow-hidden"
          onClick={(e) => e.stopPropagation()}
          data-testid="edit-profile-modal"
        >
          {/* Header */}
          <div
            className="px-6 py-5 text-white relative"
            style={{ background: `linear-gradient(135deg, ${PRIMARY}, #4F1B7C)` }}
          >
            <button
              type="button"
              onClick={onClose}
              className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center transition"
              aria-label="Fermer"
              data-testid="edit-profile-close"
            >
              <X size={16} />
            </button>
            <div className="text-pink-300 font-black text-[10px] tracking-[0.3em] uppercase mb-1">
              Mon Espace
            </div>
            <h2
              className="font-black uppercase italic text-2xl"
              style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: "0.04em" }}
            >
              Mes <span style={{ color: SECONDARY }}>coordonnées</span>
            </h2>
            <p className="text-[12px] text-white/70 mt-1">
              Mettez à jour vos informations personnelles.
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-6 space-y-4" data-testid="edit-profile-form">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-pink-600 font-black text-[10px] uppercase tracking-widest flex items-center gap-1.5">
                  <User size={11} /> Prénom
                </label>
                <input
                  required
                  value={form.first_name}
                  onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                  className="mt-1 w-full px-3 py-2.5 rounded-xl bg-slate-50 border border-slate-200 focus:border-pink-500 focus:bg-white focus:outline-none transition text-sm"
                  data-testid="edit-input-firstname"
                />
              </div>
              <div>
                <label className="text-pink-600 font-black text-[10px] uppercase tracking-widest flex items-center gap-1.5">
                  <User size={11} /> Nom
                </label>
                <input
                  required
                  value={form.last_name}
                  onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                  className="mt-1 w-full px-3 py-2.5 rounded-xl bg-slate-50 border border-slate-200 focus:border-pink-500 focus:bg-white focus:outline-none transition text-sm"
                  data-testid="edit-input-lastname"
                />
              </div>
            </div>

            <div>
              <label className="text-pink-600 font-black text-[10px] uppercase tracking-widest flex items-center gap-1.5">
                <Phone size={11} /> Téléphone
              </label>
              <input
                type="tel"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                placeholder="06 XX XX XX XX"
                className="mt-1 w-full px-3 py-2.5 rounded-xl bg-slate-50 border border-slate-200 focus:border-pink-500 focus:bg-white focus:outline-none transition text-sm"
                data-testid="edit-input-phone"
              />
            </div>

            {/* Email — lecture seule */}
            <div>
              <label className="text-slate-500 font-black text-[10px] uppercase tracking-widest flex items-center gap-1.5">
                <AtSign size={11} /> Email <Lock size={9} className="text-slate-400" />
              </label>
              <input
                type="email"
                disabled
                value={customer?.email || ""}
                className="mt-1 w-full px-3 py-2.5 rounded-xl bg-slate-100 border border-slate-200 text-slate-500 cursor-not-allowed text-sm"
                data-testid="edit-input-email"
              />
              <p className="text-[10px] text-slate-400 mt-1 italic">
                Pour changer d'email, contactez le club.
              </p>
            </div>

            {/* Newsletter */}
            <label className="flex items-center gap-3 p-3 rounded-xl bg-slate-50 border border-slate-200 cursor-pointer hover:bg-slate-100 transition">
              <input
                type="checkbox"
                checked={form.accept_newsletter}
                onChange={(e) => setForm({ ...form, accept_newsletter: e.target.checked })}
                className="w-4 h-4 accent-pink-500"
                data-testid="edit-input-newsletter"
              />
              <div className="flex-1">
                <div className="font-black text-[12px] text-slate-800 flex items-center gap-1.5">
                  <Mail size={11} className="text-pink-500" />
                  Newsletter du club
                </div>
                <div className="text-[10px] text-slate-500">
                  Recevez les actus, prochains matchs et offres exclusives.
                </div>
              </div>
            </label>

            {error && (
              <div className="rounded-lg bg-rose-50 text-rose-700 text-[12px] px-3 py-2 border border-rose-200">
                {error}
              </div>
            )}

            <div className="flex items-center gap-2 pt-2">
              <button
                type="button"
                onClick={onClose}
                disabled={busy}
                className="flex-1 px-4 py-2.5 rounded-full bg-slate-100 text-slate-600 font-black uppercase tracking-widest text-[11px] hover:bg-slate-200 transition disabled:opacity-50"
                data-testid="edit-profile-cancel"
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={busy}
                className="flex-1 inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-full text-white font-black uppercase tracking-widest text-[11px] shadow-lg hover:scale-[1.02] transition disabled:opacity-60"
                style={{ background: `linear-gradient(135deg, ${SECONDARY}, #ff3a8c)` }}
                data-testid="edit-profile-submit"
              >
                <Save size={12} />
                {busy ? "..." : "Enregistrer"}
              </button>
            </div>
          </form>
        </motion.div>
      </motion.div>
    </AnimatePresence>,
    document.body
  );
}
